import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn";

const iconButton = cva(
  [
    "inline-flex items-center justify-center shrink-0",
    "transition-[background-color,color] duration-[var(--duration-micro)] ease-[var(--ease-out)]",
    "disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer",
    "[&_svg]:shrink-0",
  ],
  {
    variants: {
      intent: {
        ghost: "bg-transparent text-fg-muted hover:bg-surface-hover hover:text-fg",
        secondary: "bg-surface text-fg border border-border-default hover:bg-surface-hover",
      },
      size: {
        sm: "h-7 w-7 rounded-[var(--radius-sm)] [&_svg]:size-3.5",
        md: "h-8 w-8 rounded-[var(--radius-md)] [&_svg]:size-4",
        lg: "h-10 w-10 rounded-[var(--radius-md)] [&_svg]:size-5",
      },
    },
    defaultVariants: { intent: "ghost", size: "md" },
  },
);

export interface IconButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof iconButton> {
  "aria-label": string;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ className, intent, size, type, ...props }, ref) => (
    <button
      ref={ref}
      type={type ?? "button"}
      className={cn(iconButton({ intent, size }), className)}
      {...props}
    />
  ),
);
IconButton.displayName = "IconButton";
